import React, { useState, useRef } from 'react';
import PageInstructions from "@/components/PageInstructions";
import { Plus, Trash2, Calculator, History, Download, HelpCircle, X, Share2, FileText, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/hooks/use-toast';
import html2canvas from 'html2canvas';

interface Loteria {
  id: number;
  nome: string;
  milhar: string;
  chave: string;
  horario: string;
}

interface OperacaoResultado {
  operacao: string;
  simbolo: string;
  resultadoPrincipal: string;
  numeros: string[];
  grupos: { numero: string; nome: string }[];
  cor: string;
}

interface Resultado {
  loteria: string;
  horario: string;
  milhar: string;
  chave: string;
  operacoes: OperacaoResultado[];
  timestamp: string;
}

const CalculadoraChaveOuro = () => {
  const [loterias, setLoterias] = useState<Loteria[]>([
    { id: 1, nome: '', milhar: '', chave: '', horario: '' }
  ]);
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [historico, setHistorico] = useState<Resultado[]>([]);
  const [mostrarTutorial, setMostrarTutorial] = useState(false);
  const resultadosRef = useRef<HTMLDivElement>(null);

  const loteriasDisponiveis = [
    'Paratodos', 'Look', 'Goiás', 'Federal', 'Maluquinha', 'Minas Gerais',
    'Bahia', 'Bahia Maluca', 'Capital', 'Monte Carlos', 'LOTECE/LOTEP',
    'Nacional', 'São Paulo PT', 'Rio', 'LBR', 'Coruja'
  ];

  const gruposBicho: Record<number, { nome: string; dezenas: string[] }> = {
    1: { nome: "Avestruz", dezenas: ["01","02","03","04"] },
    2: { nome: "Águia", dezenas: ["05","06","07","08"] },
    3: { nome: "Burro", dezenas: ["09","10","11","12"] },
    4: { nome: "Borboleta", dezenas: ["13","14","15","16"] },
    5: { nome: "Cachorro", dezenas: ["17","18","19","20"] },
    6: { nome: "Cabra", dezenas: ["21","22","23","24"] },
    7: { nome: "Carneiro", dezenas: ["25","26","27","28"] },
    8: { nome: "Camelo", dezenas: ["29","30","31","32"] },
    9: { nome: "Cobra", dezenas: ["33","34","35","36"] },
    10: { nome: "Coelho", dezenas: ["37","38","39","40"] },
    11: { nome: "Cavalo", dezenas: ["41","42","43","44"] },
    12: { nome: "Elefante", dezenas: ["45","46","47","48"] },
    13: { nome: "Galo", dezenas: ["49","50","51","52"] },
    14: { nome: "Gato", dezenas: ["53","54","55","56"] },
    15: { nome: "Jacaré", dezenas: ["57","58","59","60"] },
    16: { nome: "Leão", dezenas: ["61","62","63","64"] },
    17: { nome: "Macaco", dezenas: ["65","66","67","68"] },
    18: { nome: "Porco", dezenas: ["69","70","71","72"] },
    19: { nome: "Pavão", dezenas: ["73","74","75","76"] },
    20: { nome: "Peru", dezenas: ["77","78","79","80"] },
    21: { nome: "Touro", dezenas: ["81","82","83","84"] },
    22: { nome: "Tigre", dezenas: ["85","86","87","88"] },
    23: { nome: "Urso", dezenas: ["89","90","91","92"] },
    24: { nome: "Veado", dezenas: ["93","94","95","96"] },
    25: { nome: "Vaca", dezenas: ["97","98","99","00"] }
  };

  const adicionarLoteria = () => {
    setLoterias([...loterias, { 
      id: Date.now(), 
      nome: '', 
      milhar: '', 
      chave: '', 
      horario: '' 
    }]);
  };

  const removerLoteria = (id: number) => {
    if (loterias.length > 1) {
      setLoterias(loterias.filter(lot => lot.id !== id));
    }
  };

  const atualizarLoteria = (id: number, campo: keyof Loteria, valor: string) => {
    setLoterias(loterias.map(lot => 
      lot.id === id ? { ...lot, [campo]: valor } : lot
    ));
  };

  const formatarHorario = (value: string) => {
    // Remove tudo que não é número
    const numeros = value.replace(/\D/g, '');
    
    // Se tiver mais de 2 dígitos, insere os dois pontos
    if (numeros.length >= 2) {
      return numeros.slice(0, 2) + ':' + numeros.slice(2, 4);
    }
    return numeros;
  };

  const getGrupoPorDezena = (dezena: string) => {
    const dez = dezena.padStart(2, '0');
    for (const [grupo, dados] of Object.entries(gruposBicho)) {
      if (dados.dezenas.includes(dez)) {
        return { numero: grupo, nome: dados.nome };
      }
    }
    return null;
  };

  const gerarNumerosDeOperacao = (resultadoBase: number) => {
    const baseStr = Math.abs(resultadoBase).toString().padStart(4, '0').slice(-4);
    const d1 = parseInt(baseStr[0]);
    const d2 = parseInt(baseStr[1]);
    const d3 = parseInt(baseStr[2]);
    const d4 = parseInt(baseStr[3]);

    const menos = [
      Math.abs(d1 - d2).toString() + Math.abs(d3 - d4).toString(),
      Math.abs(d2 - d3).toString() + Math.abs(d1 - d4).toString()
    ];

    const vezes = [
      ((d1 * d2) % 100).toString().padStart(2, '0'),
      ((d3 * d4) % 100).toString().padStart(2, '0'),
      ((d1 * d3) % 100).toString().padStart(2, '0'),
      ((d2 * d4) % 100).toString().padStart(2, '0')
    ];

    const dividido = [
      d2 !== 0 ? Math.floor((d1 * 10) / d2).toString().padStart(2, '0') : '00',
      d4 !== 0 ? Math.floor((d3 * 10) / d4).toString().padStart(2, '0') : '00',
      d3 !== 0 ? Math.floor((d1 * 10) / d3).toString().padStart(2, '0') : '00',
      d1 !== 0 ? Math.floor((d4 * 10) / d1).toString().padStart(2, '0') : '00'
    ];

    return [
      baseStr,
      ...menos,
      ...vezes,
      ...dividido
    ].filter((v, i, a) => a.indexOf(v) === i);
  };

  const calcularResultados = () => {
    const novosResultados: Resultado[] = [];
    
    loterias.forEach(loteria => {
      if (!loteria.milhar || !loteria.chave || !loteria.nome) return;

      const milhar = parseInt(loteria.milhar);
      const chave = parseInt(loteria.chave);
      
      // 4 operações principais
      const operacoesConfig = [
        { operacao: 'Soma', simbolo: '+', resultado: milhar + chave, cor: 'from-green-500 to-emerald-600' },
        { operacao: 'Subtração', simbolo: '-', resultado: Math.abs(milhar - chave), cor: 'from-red-500 to-rose-600' },
        { operacao: 'Multiplicação', simbolo: '×', resultado: (milhar * chave) % 10000, cor: 'from-blue-500 to-indigo-600' },
        { operacao: 'Divisão', simbolo: '÷', resultado: chave !== 0 ? Math.floor(milhar / chave) : 0, cor: 'from-purple-500 to-violet-600' }
      ];

      const operacoes: OperacaoResultado[] = operacoesConfig.map(op => {
        const numeros = gerarNumerosDeOperacao(op.resultado);
        const grupos = numeros.map(num => {
          const dezena = num.slice(-2);
          return getGrupoPorDezena(dezena);
        }).filter(Boolean) as { numero: string; nome: string }[];

        return {
          operacao: op.operacao,
          simbolo: op.simbolo,
          resultadoPrincipal: op.resultado.toString().padStart(4, '0').slice(-4),
          numeros,
          grupos,
          cor: op.cor
        };
      });

      novosResultados.push({
        loteria: loteria.nome,
        horario: loteria.horario,
        milhar: loteria.milhar,
        chave: loteria.chave,
        operacoes,
        timestamp: new Date().toLocaleString('pt-BR')
      });
    });

    setResultados(novosResultados);
    
    if (novosResultados.length > 0) {
      setHistorico([...historico, ...novosResultados]);
    }
  };

  const limparResultados = () => {
    setResultados([]);
  };

  const exportarHistorico = () => {
    const dados = JSON.stringify(historico, null, 2);
    const blob = new Blob([dados], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historico-calculadora-chave-ouro-${Date.now()}.json`;
    a.click();
  };

  const exportarTXT = () => {
    if (resultados.length === 0) {
      toast({ title: "Sem resultados", description: "Calcule os resultados primeiro!", variant: "destructive" });
      return;
    }

    let texto = "=== CALCULADORA CHAVE DE OURO ===\n";
    texto += `Data: ${new Date().toLocaleString('pt-BR')}\n\n`;

    resultados.forEach((resultado) => {
      texto += `--- ${resultado.loteria} ${resultado.horario ? `- ${resultado.horario}` : ''} ---\n`;
      texto += `Milhar: ${resultado.milhar} | Chave: ${resultado.chave}\n\n`;

      resultado.operacoes.forEach((op) => {
        texto += `[${op.operacao}] ${resultado.milhar} ${op.simbolo} ${resultado.chave} = ${op.resultadoPrincipal}\n`;
        texto += `Números: ${op.numeros.join(', ')}\n`;
        texto += `Grupos: ${op.grupos.map(g => `G${g.numero} ${g.nome}`).join(', ')}\n\n`;
      });
      texto += "\n";
    });

    const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jogos-chave-ouro-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Arquivo baixado!", description: "Jogos exportados com sucesso." });
  };

  const compartilharImagem = async () => {
    if (!resultadosRef.current || resultados.length === 0) {
      toast({ title: "Sem resultados", description: "Calcule os resultados primeiro!", variant: "destructive" });
      return;
    }

    try {
      const canvas = await html2canvas(resultadosRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });

      canvas.toBlob(async (blob) => {
        if (!blob) return;

        if (navigator.share && navigator.canShare) {
          const file = new File([blob], 'jogos-chave-ouro.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'Jogos Chave de Ouro',
              text: 'Confira os jogos gerados pela Calculadora Chave de Ouro!',
              files: [file]
            });
            return;
          }
        }

        // Fallback: baixar imagem
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `jogos-chave-ouro-${new Date().toISOString().split('T')[0]}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: "Imagem baixada!", description: "Use a imagem para compartilhar." });
      }, 'image/png');
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível gerar a imagem.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <PageInstructions
        title="📖 Como usar a Calculadora Chave de Ouro"
        steps={[
          "Insira os números dos resultados anteriores nos campos indicados",
          "Adicione mais números clicando no botão '+'",
          "Clique em 'Calcular' para gerar a chave de ouro",
          "O sistema soma e processa os números para encontrar os palpites",
          "Veja os grupos e dezenas sugeridos no resultado",
          "Use o botão de exportar para salvar ou compartilhar"
        ]}
      />
      {/* Header */}
      <Card className="bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 border-0 shadow-xl">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                🔑 Calculadora Chave de Ouro
              </CardTitle>
              <p className="text-white/90 mt-2 text-sm md:text-base">
                Sistema de cálculo com chave mágica para todas as loterias
              </p>
            </div>
            <Button
              onClick={() => setMostrarTutorial(true)}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Tutorial
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Chaves de Ouro Recomendadas */}
      <Card className="shadow-lg border-2 border-amber-300">
        <CardHeader className="pb-3 bg-gradient-to-r from-amber-100 to-yellow-100">
          <CardTitle className="text-lg md:text-xl font-bold text-amber-800 flex items-center gap-2">
            🔑 Chaves de Ouro Recomendadas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {/* Chaves Principais */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border-2 border-blue-300">
              <div className="text-center">
                <span className="text-3xl font-bold text-blue-700">469</span>
                <p className="text-sm text-blue-600 mt-2 font-medium">Melhor para:</p>
                <div className="flex flex-wrap justify-center gap-1 mt-1">
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">Rio</span>
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">Look</span>
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">Nacional</span>
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">São Paulo</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-2 border-green-300">
              <div className="text-center">
                <span className="text-3xl font-bold text-green-700">385</span>
                <p className="text-sm text-green-600 mt-2 font-medium">Para achar grupos na cabeça:</p>
                <div className="flex flex-wrap justify-center gap-1 mt-1">
                  <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">São Paulo</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border-2 border-purple-300">
              <div className="text-center">
                <span className="text-3xl font-bold text-purple-700">748</span>
                <p className="text-sm text-purple-600 mt-2 font-medium">Melhor para:</p>
                <div className="flex flex-wrap justify-center gap-1 mt-1">
                  <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">Nacional</span>
                  <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">São Paulo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Chaves Extras */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-2 border-orange-300">
            <p className="text-sm font-bold text-orange-800 mb-3 flex items-center gap-2">
              ⚡ Chaves Extras Mais Fortes:
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-4 py-2 rounded-lg shadow-md">1415</span>
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-4 py-2 rounded-lg shadow-md">3908</span>
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-4 py-2 rounded-lg shadow-md">0663</span>
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-4 py-2 rounded-lg shadow-md">774</span>
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-4 py-2 rounded-lg shadow-md">748</span>
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-4 py-2 rounded-lg shadow-md">358</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Tutorial */}
      <Dialog open={mostrarTutorial} onOpenChange={setMostrarTutorial}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-lg">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              📚 Tutorial Completo do Sistema
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[70vh] p-6">
            <div className="space-y-6">
              {/* Passo 1 */}
              <div className="border-l-4 border-amber-500 pl-4">
                <h3 className="text-xl font-bold text-amber-800 mb-3">📍 Passo 1: Entenda o Sistema</h3>
                <div className="bg-amber-50 p-4 rounded-lg space-y-2">
                  <p className="text-gray-700">Este sistema gera números baseado em uma <strong>fórmula matemática</strong> que combina:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li><strong>Milhar da Cabeça:</strong> O resultado de 4 dígitos do sorteio (ex: 8191)</li>
                    <li><strong>Chave Mágica:</strong> Número especial do dia (ex: 1415 para dia 15)</li>
                    <li><strong>Operações:</strong> Soma + operações matemáticas = números gerados</li>
                  </ul>
                </div>
              </div>

              {/* Passo 2 */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-xl font-bold text-blue-800 mb-3">🎯 Passo 2: Selecione a Loteria</h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-3">Escolha qual loteria você quer calcular no dropdown:</p>
                  <div className="bg-white p-3 rounded border-2 border-blue-300">
                    <span className="text-sm text-gray-600">Opções: </span>
                    <span className="font-semibold text-blue-700">Paratodos • Look • Federal • Bahia • Rio • Nacional • e mais...</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">💡 <strong>Dica:</strong> Você pode adicionar várias loterias ao mesmo tempo!</p>
                </div>
              </div>

              {/* Passo 3 */}
              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="text-xl font-bold text-yellow-800 mb-3">🔢 Passo 3: Digite a Milhar</h3>
                <div className="bg-yellow-50 p-4 rounded-lg space-y-3">
                  <p className="text-gray-700">A <strong>milhar da cabeça</strong> é o resultado principal do sorteio (4 dígitos):</p>
                  <div className="bg-white p-4 rounded-lg border-2 border-yellow-300">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Exemplo 1:</p>
                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white p-3 rounded text-center font-bold text-xl">8191</div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Exemplo 2:</p>
                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white p-3 rounded text-center font-bold text-xl">5893</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Passo 4 */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-bold text-purple-800 mb-3">🔑 Passo 4: Digite a Chave Mágica</h3>
                <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                  <p className="text-gray-700">A <strong>chave mágica</strong> é um número especial que você define (geralmente relacionado à data):</p>
                  <div className="bg-white p-4 rounded-lg border-2 border-purple-300 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Dia 15:</span>
                      <span className="bg-purple-600 text-white px-4 py-2 rounded font-bold">1415</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Dia 20:</span>
                      <span className="bg-purple-600 text-white px-4 py-2 rounded font-bold">2020</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exemplo Prático */}
              <div className="bg-gradient-to-r from-orange-100 to-yellow-100 p-6 rounded-xl border-2 border-orange-300">
                <h3 className="text-xl font-bold text-orange-800 mb-4">💡 Exemplo Prático Completo</h3>
                <div className="bg-white p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Loteria:</p>
                      <p className="font-bold text-lg text-blue-700">Look</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Horário:</p>
                      <p className="font-bold text-lg text-blue-700">08:40</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Milhar:</p>
                      <p className="font-bold text-lg text-green-700">8191</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Chave:</p>
                      <p className="font-bold text-lg text-purple-700">1415</p>
                    </div>
                  </div>
                  <div className="pt-3 border-t-2 border-gray-200">
                    <p className="text-sm text-gray-600 mb-2">Resultado da soma:</p>
                    <p className="font-bold text-2xl text-orange-600">8191 + 1415 = 9606</p>
                  </div>
                </div>
              </div>

              {/* Botão Fechar */}
              <div className="text-center pt-4">
                <Button
                  onClick={() => setMostrarTutorial(false)}
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold px-8 py-3"
                >
                  ✅ Entendi! Vamos Começar
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Formulário de Entrada */}
      <Card className="shadow-lg">
        <CardContent className="p-4 md:p-6">
          <div className="space-y-4">
            {loterias.map((loteria, index) => (
              <div key={loteria.id} className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border-2 border-amber-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">Loteria</Label>
                    <Select
                      value={loteria.nome}
                      onValueChange={(value) => atualizarLoteria(loteria.id, 'nome', value)}
                    >
                      <SelectTrigger className="bg-white text-gray-900 border-gray-300">
                        <SelectValue placeholder="Selecione..." className="text-gray-900" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                        {loteriasDisponiveis.map(nome => (
                          <SelectItem key={nome} value={nome} className="text-gray-900 hover:bg-gray-100 cursor-pointer">{nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">Milhar</Label>
                    <Input
                      type="text"
                      value={loteria.milhar}
                      onChange={(e) => atualizarLoteria(loteria.id, 'milhar', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="8191"
                      maxLength={4}
                      className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">Chave Mágica</Label>
                    <Input
                      type="text"
                      value={loteria.chave}
                      onChange={(e) => atualizarLoteria(loteria.id, 'chave', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="1415"
                      maxLength={4}
                      className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">Horário</Label>
                    <Input
                      type="text"
                      value={loteria.horario}
                      onChange={(e) => atualizarLoteria(loteria.id, 'horario', formatarHorario(e.target.value))}
                      placeholder="08:40"
                      maxLength={5}
                      className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      onClick={() => removerLoteria(loteria.id)}
                      disabled={loterias.length === 1}
                      variant="destructive"
                      className="w-full"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remover
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Botões de Ação */}
          <div className="flex flex-wrap gap-3 mt-6">
            <Button
              onClick={adicionarLoteria}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Loteria
            </Button>

            <Button
              onClick={calcularResultados}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calcular Resultados
            </Button>

            <Button
              onClick={limparResultados}
              variant="secondary"
            >
              Limpar
            </Button>

            {historico.length > 0 && (
              <Button
                onClick={exportarHistorico}
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50 ml-auto"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Histórico ({historico.length})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resultados */}
      {resultados.length > 0 && (
        <div className="space-y-4">
          {/* Botões de Compartilhar/Baixar */}
          <Card className="shadow-lg border-2 border-green-300">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 justify-center">
                <Button
                  onClick={compartilharImagem}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartilhar Imagem
                </Button>
                <Button
                  onClick={exportarTXT}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Baixar TXT
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Cards dos Resultados */}
          <div ref={resultadosRef} className="space-y-8 bg-white p-4 rounded-lg">
            {resultados.map((resultado, index) => (
              <Card key={index} className="shadow-xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white pb-4">
                  <CardTitle className="text-xl md:text-2xl font-bold">
                    {resultado.loteria} {resultado.horario && `- ${resultado.horario}`}
                  </CardTitle>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-white/90">
                    <span>Milhar: <strong className="text-white">{resultado.milhar}</strong></span>
                    <span>Chave: <strong className="text-white">{resultado.chave}</strong></span>
                  </div>
                </CardHeader>
                <CardContent className="p-4 md:p-6 space-y-6">
                  {/* Resultados por Operação */}
                  {resultado.operacoes.map((op, opIndex) => (
                    <div key={opIndex} className="border rounded-xl overflow-hidden">
                      {/* Header da Operação */}
                      <div className={`bg-gradient-to-r ${op.cor} text-white p-4`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-lg font-bold flex items-center gap-2">
                            <span className="bg-white/20 px-3 py-1 rounded-full text-xl">{op.simbolo}</span>
                            {op.operacao}
                          </h3>
                          <div className="text-sm">
                            {resultado.milhar} {op.simbolo} {resultado.chave} = <strong className="text-xl">{op.resultadoPrincipal}</strong>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-gray-50">
                        {/* Números Gerados */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Números Gerados:</h4>
                          <div className="flex flex-wrap gap-2">
                            {op.numeros.map((num, i) => (
                              <div key={i} className={`bg-gradient-to-br ${op.cor} text-white font-bold text-center py-2 px-3 rounded-lg shadow-md text-sm`}>
                                {num}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Grupos do Bicho */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Grupos do Bicho:</h4>
                          <div className="flex flex-wrap gap-2">
                            {op.grupos.map((grupo, i) => (
                              <div key={i} className="bg-white border-2 border-gray-200 px-3 py-2 rounded-lg shadow-sm">
                                <span className="font-bold text-gray-800">G{grupo.numero}</span>
                                <span className="text-gray-600 ml-1 text-sm">{grupo.nome}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-xs text-gray-500 text-right pt-2 border-t">
                    Gerado em: {resultado.timestamp}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Instruções */}
      <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-amber-800 mb-3 flex items-center gap-2">
            📋 Como Usar:
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Selecione a loteria desejada no dropdown</li>
            <li>Digite a milhar da cabeça do sorteio (ex: 8191)</li>
            <li>Digite a chave mágica do dia (ex: 1415)</li>
            <li>Opcionalmente, adicione o horário do sorteio</li>
            <li>Clique em "Adicionar Loteria" para incluir mais loterias</li>
            <li>Clique em "Calcular Resultados" para gerar os números</li>
            <li>O sistema mostra números gerados e grupos correspondentes</li>
            <li>Use "Exportar Histórico" para salvar seus cálculos</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalculadoraChaveOuro;
